import React, { useState, useEffect } from 'react';
import { tourLegApi, tourApi, transportLegApi } from '../api/client';

// Since the split, a tour leg is the composition of a tour: which
// reisonderdeel in which position, valid in which period. The leg itself, with
// stations, company and price type, lives in transport_leg and is only picked
// here.
const emptyForm = {
  tour_id: '',
  sequence_number: 1,
  transport_leg_id: '',
  outward_return: 'outward',
  valid_from: '',
  valid_to: '',
};

function TourLegForm() {
  const [tourLegs, setTourLegs] = useState([]);
  const [tours, setTours] = useState([]);
  const [transportLegs, setTransportLegs] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchTourLegs();
    fetchTours();
    fetchTransportLegs();
  }, []);

  const fetchTourLegs = async () => {
    try {
      const response = await tourLegApi.list();
      setTourLegs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch tour legs' });
    }
  };

  const fetchTours = async () => {
    try {
      const response = await tourApi.list();
      setTours(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch tours' });
    }
  };

  const fetchTransportLegs = async () => {
    try {
      const response = await transportLegApi.list();
      setTransportLegs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch transport legs' });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      tour_id: parseInt(formData.tour_id),
      sequence_number: parseInt(formData.sequence_number),
      transport_leg_id: parseInt(formData.transport_leg_id),
      outward_return: formData.outward_return,
      // Leaving it empty means: valid today, open-ended. The database then
      // fills in valid_from itself.
      valid_from: formData.valid_from || null,
      valid_to: formData.valid_to || null,
    };

    try {
      await tourLegApi.create(submitData);
      setMessage({ type: 'success', text: 'Tour leg created successfully' });
      setFormData(emptyForm);
      fetchTourLegs();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create tour leg' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await tourLegApi.delete(id);
        setMessage({ type: 'success', text: 'Tour leg deleted' });
        fetchTourLegs();
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to delete tour leg' });
      }
    }
  };

  const tourName = (id) => {
    const t = tours.find((t) => t.id === id);
    return t ? t.name_nl : `#${id}`;
  };

  const transportLeg = (id) => transportLegs.find((l) => l.id === id);

  const legLabel = (leg) => {
    if (!leg) return '';
    const company = leg.operator ? ` · ${leg.operator}` : '';
    const sleeper = leg.sleeper_train ? ' · night' : '';
    return `${leg.origin} → ${leg.destination}${company}${sleeper}`;
  };

  const legRoute = (id) => {
    const leg = transportLeg(id);
    return leg ? `${leg.origin} → ${leg.destination}` : `#${id}`;
  };

  const legCompany = (id) => {
    const leg = transportLeg(id);
    return leg && leg.operator ? leg.operator : '-';
  };

  const validity = (leg) => {
    if (!leg.valid_to) return `from ${leg.valid_from}`;
    return `${leg.valid_from} → ${leg.valid_to}`;
  };

  // Per tour in the order the traveller rides them, so a composition reads
  // from top to bottom. Sorted on the tour name as shown, then the sequence
  // number; validity and id only break ties between successive compositions.
  const sortedLegs = [...tourLegs].sort(
    (a, b) =>
      tourName(a.tour_id).localeCompare(tourName(b.tour_id), 'nl') ||
      a.sequence_number - b.sequence_number ||
      String(a.valid_from).localeCompare(String(b.valid_from)) ||
      a.id - b.id
  );

  return (
    <div>
      <h2>Manage Tour Legs</h2>

      {message && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Tour</label>
          <select
            name="tour_id"
            value={formData.tour_id}
            onChange={handleChange}
            required
          >
            <option value="">Select a tour...</option>
            {tours.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name_nl}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Sequence Number</label>
          <input
            type="number"
            name="sequence_number"
            value={formData.sequence_number}
            onChange={handleChange}
            min="1"
            required
          />
        </div>

        <div className="form-group">
          <label>Transport Leg</label>
          <select
            name="transport_leg_id"
            value={formData.transport_leg_id}
            onChange={handleChange}
            required
          >
            <option value="">Select a transport leg...</option>
            {transportLegs.map((leg) => (
              <option key={leg.id} value={leg.id}>
                {legLabel(leg)}
              </option>
            ))}
          </select>
          {transportLegs.length === 0 && (
            <small>No transport legs yet. They are loaded from the spreadsheet.</small>
          )}
        </div>

        <div className="form-group">
          <label>Direction</label>
          <select
            name="outward_return"
            value={formData.outward_return}
            onChange={handleChange}
          >
            <option value="outward">Outward</option>
            <option value="return">Return</option>
          </select>
        </div>

        <div className="form-group">
          <label>Valid from</label>
          <input
            type="date"
            name="valid_from"
            value={formData.valid_from}
            onChange={handleChange}
          />
          <small>Leave empty for today.</small>
        </div>

        <div className="form-group">
          <label>Valid to</label>
          <input
            type="date"
            name="valid_to"
            value={formData.valid_to}
            onChange={handleChange}
          />
          <small>Leave empty while this is the current composition.</small>
        </div>

        <button type="submit">Create Tour Leg</button>
      </form>

      <h3>Existing Tour Legs</h3>
      {tourLegs.length === 0 ? (
        <p>No tour legs yet</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Tour</th>
              <th>Seq</th>
              <th>Route</th>
              <th>Dir</th>
              <th>Operator</th>
              <th>Valid</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedLegs.map((leg) => (
              <tr key={leg.id}>
                <td>{leg.id}</td>
                <td>{tourName(leg.tour_id)}</td>
                <td>{leg.sequence_number}</td>
                <td>{legRoute(leg.transport_leg_id)}</td>
                <td>{leg.outward_return}</td>
                <td>{legCompany(leg.transport_leg_id)}</td>
                <td>{validity(leg)}</td>
                <td>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(leg.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default TourLegForm;
